'use client';

/**
 * Individual chat message component with enhanced UI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType, FileAttachment } from '../types/support-chat';
import type { Components } from 'react-markdown';

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
}

/**
 * Get avatar initials from a name
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Get avatar background color based on sender type
 */
function getAvatarColor(senderType: string): string {
  switch (senderType) {
    case 'user':
      return '#3b82f6'; // Blue-500
    case 'support':
      return '#059669'; // Emerald-600 for better contrast
    case 'system':
      return '#6b7280'; // Gray-500
    default:
      return '#9ca3af'; // Gray-400
  }
}

/**
 * Get message bubble colors based on sender type
 */
function getMessageColors(senderType: string, isUser: boolean) {
  if (isUser || senderType === 'user') {
    return {
      backgroundColor: '#3b82f6',
      textColor: '#ffffff',
      timestampColor: 'rgba(255, 255, 255, 0.8)',
      nameColor: 'rgba(255, 255, 255, 0.9)',
    };
  }

  if (senderType === 'support') {
    return {
      backgroundColor: '#059669', // Emerald-600 for better contrast with white text
      textColor: '#ffffff',
      timestampColor: 'rgba(255, 255, 255, 0.85)',
      nameColor: 'rgba(255, 255, 255, 0.95)',
    };
  }

  // System messages
  return {
    backgroundColor: '#f3f4f6',
    textColor: '#111827',
    timestampColor: '#6b7280',
    nameColor: '#6b7280',
  };
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Check if file is an image
 */
function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Get markdown components with inline styles matching chat bubble design
 */
function getMarkdownComponents(textColor: string, isUser: boolean): Partial<Components> {
  const linkColor = isUser ? 'rgba(255, 255, 255, 0.9)' : '#3b82f6';
  const codeBg = isUser ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)';
  const codeColor = textColor;
  const borderColor = isUser ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)';

  return {
    p: ({ children }) => (
      <p style={{ margin: '0 0 8px 0', color: textColor }}>
        {children}
      </p>
    ),
    // Headings
    h1: ({ children }) => (
      <h1 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: textColor }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: textColor }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: 600, color: textColor }}>
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: textColor }}>
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: textColor }}>
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: textColor }}>
        {children}
      </h6>
    ),
    // Lists
    ul: ({ children }) => (
      <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px', listStyleType: 'disc', color: textColor }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '0 0 8px 0', paddingLeft: '20px', listStyleType: 'decimal', color: textColor }}>
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li style={{ margin: '2px 0', color: textColor }}>
        {children}
      </li>
    ),
    // Links
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: linkColor,
          textDecoration: 'underline',
          textDecorationColor: isUser ? 'rgba(255, 255, 255, 0.5)' : 'rgba(59, 130, 246, 0.5)',
        }}
      >
        {children}
      </a>
    ),
    // Code
    code: ({ children, className }) => {
      const isInline = !className;
      return (
        <code
          style={{
            backgroundColor: codeBg,
            color: codeColor,
            padding: isInline ? '2px 4px' : '8px',
            borderRadius: '4px',
            fontSize: isInline ? '13px' : '12px',
            fontFamily: 'monospace',
            display: isInline ? 'inline' : 'block',
            overflow: 'auto',
            margin: isInline ? '0' : '8px 0',
            border: isInline ? 'none' : `1px solid ${borderColor}`,
          }}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre
        style={{
          margin: '8px 0',
          padding: '8px',
          backgroundColor: codeBg,
          borderRadius: '4px',
          overflow: 'auto',
          border: `1px solid ${borderColor}`,
        }}
      >
        {children}
      </pre>
    ),
    // Blockquote
    blockquote: ({ children }) => (
      <blockquote
        style={{
          margin: '8px 0',
          paddingLeft: '12px',
          borderLeft: `3px solid ${borderColor}`,
          color: textColor,
          opacity: 0.9,
        }}
      >
        {children}
      </blockquote>
    ),
    // Text formatting
    strong: ({ children }) => (
      <strong style={{ fontWeight: 600, color: textColor }}>
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em style={{ fontStyle: 'italic', color: textColor }}>
        {children}
      </em>
    ),
    // Horizontal rule
    hr: () => (
      <hr
        style={{
          margin: '8px 0',
          border: 'none',
          borderTop: `1px solid ${borderColor}`,
        }}
      />
    ),
    // Tables (from remark-gfm)
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '8px 0' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            color: textColor,
          }}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead style={{ borderBottom: `2px solid ${borderColor}` }}>
        {children}
      </thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th
        style={{
          padding: '6px 8px',
          textAlign: 'left',
          fontWeight: 600,
          color: textColor,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          padding: '6px 8px',
          color: textColor,
        }}
      >
        {children}
      </td>
    ),
  };
}

/**
 * File attachment component
 */
function FileAttachmentItem({ attachment, isUser }: { attachment: FileAttachment; isUser: boolean }) {
  const isImage = isImageFile(attachment.mimeType);
  const linkColor = isUser ? 'rgba(255, 255, 255, 0.9)' : '#3b82f6';

  return (
    <div
      style={{
        marginTop: '8px',
        padding: '8px',
        backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
        borderRadius: '6px',
        border: `1px solid ${isUser ? 'rgba(255, 255, 255, 0.2)' : '#e5e7eb'}`,
      }}
    >
      {isImage ? (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textDecoration: 'none',
            color: linkColor,
          }}
        >
          <img
            src={attachment.url}
            alt={attachment.name}
            style={{
              maxWidth: '200px',
              maxHeight: '200px',
              borderRadius: '4px',
              display: 'block',
            }}
            onError={(e) => {
              // Fallback if image fails to load
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div
            style={{
              marginTop: '4px',
              fontSize: '12px',
              opacity: 0.8,
            }}
          >
            {attachment.name} ({formatFileSize(attachment.size)})
          </div>
        </a>
      ) : (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            color: linkColor,
            fontSize: '13px',
          }}
        >
          <span style={{ fontSize: '16px' }}>📄</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
              title={attachment.name}
            >
              {attachment.name}
            </div>
            <div
              style={{
                fontSize: '11px',
                opacity: 0.7,
                marginTop: '2px',
              }}
            >
              {formatFileSize(attachment.size)}
            </div>
          </div>
          <span style={{ fontSize: '12px', opacity: 0.7 }}>⬇</span>
        </a>
      )}
    </div>
  );
}

export function ChatMessage({ message, isStreaming = false }: ChatMessageProps) {
  const isUser = message.isUser;
  const senderType = message.sender.type;
  const timestamp = new Date(message.timestamp);
  const formattedTime = format(timestamp, 'HH:mm');
  
  const colors = getMessageColors(senderType, isUser);
  const avatarColor = getAvatarColor(senderType);
  const initials = getInitials(message.sender.name);

  // Determine alignment: user messages right, support/system messages left
  const isRightAligned = isUser || senderType === 'user';

  // Streaming state: track how many characters to display
  const [displayedCharCount, setDisplayedCharCount] = useState(0);

  // Streaming effect: animate character by character
  useEffect(() => {
    if (!isStreaming) {
      // If not streaming, show all characters
      setDisplayedCharCount(message.text.length);
      return;
    }

    // If streaming, start from current position and add characters
    if (displayedCharCount >= message.text.length) {
      // Streaming complete
      return;
    }

    // Use requestAnimationFrame for smooth character-by-character animation
    const timer = setTimeout(() => {
      setDisplayedCharCount((prev) => Math.min(prev + 1, message.text.length));
    }, 20); // 20ms per character = ~50 characters per second

    return () => clearTimeout(timer);
  }, [isStreaming, displayedCharCount, message.text.length]);

  // Get the text to display (either full or partial for streaming)
  const displayedText = isStreaming ? message.text.substring(0, displayedCharCount) : message.text;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        marginBottom: '16px',
        justifyContent: isRightAligned ? 'flex-end' : 'flex-start',
        gap: '8px',
        width: '100%',
      }}
    >
      {/* Avatar on left for support/system messages */}
      {!isRightAligned && (
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: avatarColor,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            flexShrink: 0,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
          aria-label={`Avatar for ${message.sender.name}`}
        >
          {senderType === 'system' ? '⚙' : initials}
        </div>
      )}

      {/* Message bubble */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '75%',
          minWidth: '120px',
          alignItems: isRightAligned ? 'flex-end' : 'flex-start',
          marginLeft: isRightAligned ? 'auto' : '0',
          marginRight: isRightAligned ? '0' : '0',
        }}
      >
        {/* Sender name for support messages */}
        {!isUser && senderType !== 'system' && (
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '4px',
              color: '#6b7280',
              paddingLeft: '4px',
              paddingRight: '4px',
            }}
          >
            {message.sender.name}
          </div>
        )}

        {/* Message bubble with tail */}
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '16px',
            backgroundColor: colors.backgroundColor,
            color: colors.textColor,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            // Chat bubble tail styling
            ...(isRightAligned
              ? {
                  borderBottomRightRadius: '4px',
                  marginRight: '0',
                }
              : {
                  borderBottomLeftRadius: '4px',
                  marginLeft: '0',
                }),
          }}
        >
          {/* Message text with markdown support */}
          {displayedText && (
            <div
              style={{
                fontSize: '14px',
                lineHeight: '1.5',
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={getMarkdownComponents(colors.textColor, isUser)}
              >
                {displayedText}
              </ReactMarkdown>
            </div>
          )}

          {/* File attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div style={{ marginTop: message.text ? '8px' : '0' }}>
              {message.attachments.map((attachment) => (
                <FileAttachmentItem
                  key={attachment.id}
                  attachment={attachment}
                  isUser={isUser}
                />
              ))}
            </div>
          )}

          {/* Timestamp */}
          <div
            style={{
              fontSize: '11px',
              marginTop: '6px',
              color: colors.timestampColor,
              textAlign: isRightAligned ? 'right' : 'left',
              opacity: 0.85,
            }}
          >
            {formattedTime}
          </div>
        </div>
      </div>

      {/* Avatar on right for user messages */}
      {isRightAligned && (
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: avatarColor,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            flexShrink: 0,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
          aria-label={`Avatar for ${message.sender.name}`}
        >
          {initials}
        </div>
      )}
    </div>
  );
}

